import { FormattedMessage } from 'react-intl';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import TimestampAndMileage from '@/features/common/components/TimestampAndMileage';
import { IconWithTooltip } from '@/features/maintenance-data/components/IconWithTooltip';
import { MaintenanceDataItem } from '@/generated/GeneratedApiTypes';

const COMPONENT_TYPE_PREFIX = 'intl-msg:vehicle-diagnostics.maintenance.table.maintenance-data.type';
const LEGAL_INSPECTION_HINT = 'intl-msg:vehicle-diagnostics.maintenance.table.maintenance-data.legal-inspection-hint';
const LEGAL_INSPECTION_CIDS = new Set([50, 51, 52, 53, 54]);

const isRecurrentCustom = (row: MaintenanceDataItem) =>
    row.component_type === 'CUSTOM_COMPONENT' && row.recurrence != null;

const ComponentTypeIcon = ({ row }: { row: MaintenanceDataItem }) => {
    if (row.component_type === 'MAINTENANCE_COMPONENT') {
        return (
            <IconWithTooltip
                iconClass="rioglyph rioglyph-component"
                tooltipKey={`${COMPONENT_TYPE_PREFIX}.maintenance-component`}
                ariaLabel="Maintenance component"
            />
        );
    }
    if (isRecurrentCustom(row)) {
        return (
            <IconWithTooltip
                iconClass="rioglyph rioglyph-component-custom-recurrent"
                tooltipKey={`${COMPONENT_TYPE_PREFIX}.recurrent-custom-component`}
                ariaLabel="Recurrent custom component"
            />
        );
    }
    return (
        <IconWithTooltip
            iconClass="rioglyph rioglyph-component-custom"
            tooltipKey={`${COMPONENT_TYPE_PREFIX}.custom-component`}
            ariaLabel="Custom component"
        />
    );
};

const ComponentName = ({ row }: { row: MaintenanceDataItem }) => {
    // Custom components carry a free-text title; maintenance components carry a slug
    // (stored underscore-cased) resolved to a translated label (keys are hyphen-cased).
    const name =
        row.component_type === 'CUSTOM_COMPONENT' ? (
            <span>{row.title ?? '-'}</span>
        ) : row.component_slug ? (
            <FormattedMessage
                id={`intl-msg:vehicle-diagnostics.maintenance.component.${row.component_slug.replaceAll('_', '-')}`}
                defaultMessage={row.component_slug}
            />
        ) : (
            <span>-</span>
        );

    // Legal-inspection components (cid 50-54) carry an explanatory tooltip, mirroring WFE.
    if (row.cid != null && LEGAL_INSPECTION_CIDS.has(row.cid)) {
        return (
            <OverlayTrigger
                placement={Tooltip.TOP}
                overlay={
                    <Tooltip width={300}>
                        <FormattedMessage id={LEGAL_INSPECTION_HINT} />
                    </Tooltip>
                }
            >
                {name}
            </OverlayTrigger>
        );
    }
    return name;
};

export const ComponentCell = ({ row }: { row: MaintenanceDataItem }) => (
    <td data-field="component">
        <div className="display-flex gap-5">
            <span className="text-size-18 text-color-dark">
                <ComponentTypeIcon row={row} />
            </span>
            <div>
                <div>
                    <ComponentName row={row} />
                </div>
                <TimestampAndMileage
                    timestamp={row.measured_at?.timestamp}
                    mileage={row.measured_at?.mileage}
                    className="text-size-12"
                />
            </div>
        </div>
    </td>
);
